import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';

import { BidClassifications, RuleDefinitionData, createRuleTemplate } from '~/services/PublicOfferFeedService';

interface SaveAsTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  primaryMetrics: Array<string>;
  ruleDefinitions: RuleDefinitionData;
  ruleClassifications: BidClassifications;
}

export default function SaveAsTemplateDialog({
  open,
  onClose,
  primaryMetrics,
  ruleDefinitions,
  ruleClassifications,
}: SaveAsTemplateDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setName('');
      setDescription('');
      setNameError(null);
      onClose();
    }
  }, [onClose, isSubmitting]);

  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      setNameError('Template name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const filteredRuleDefinitions: Record<string, { H: number; M: number; L: number }> = {};
      primaryMetrics.forEach((metric) => {
        if (ruleDefinitions?.[metric]) {
          filteredRuleDefinitions[metric] = ruleDefinitions[metric];
        }
      });

      await createRuleTemplate({
        name: name.trim(),
        description: description.trim() || null,
        primary_metrics: primaryMetrics,
        rule_definitions: filteredRuleDefinitions,
        rule_classifications: ruleClassifications,
      });

      enqueueSnackbar('Template saved successfully!', { variant: 'success' });
      handleClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      enqueueSnackbar(`Failed to save template: ${errorMessage}`, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  }, [name, description, primaryMetrics, ruleDefinitions, ruleClassifications, enqueueSnackbar, handleClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save as Template</DialogTitle>
      <DialogContent>
        <Stack spacing={3} mt={1}>
          <TextField
            label="Template Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) {
                setNameError(null);
              }
            }}
            error={!!nameError}
            helperText={nameError}
            fullWidth
            required
            autoFocus
          />

          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting} variant="text">
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isSubmitting || !name.trim()} variant="contained" color="primary">
          {isSubmitting ? 'Saving...' : 'Save Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
